import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './ui.module.css';

type Variant = 'default' | 'primary' | 'soft' | 'ghost' | 'danger' | 'success';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  /** Full-width, tall target — used on the site manager's phone screens. */
  large?: boolean;
  isLoading?: boolean;
  /** Leading icon, 16px, drawn in the button's text colour. */
  icon?: ReactNode;
  children: ReactNode;
}

const VARIANT_CLASS: Record<Variant, string | undefined> = {
  default: undefined,
  primary: styles.primary,
  soft: styles.soft,
  ghost: styles.ghost,
  danger: styles.danger,
  success: styles.success,
};

export function Button({
  variant = 'default',
  large = false,
  isLoading = false,
  icon,
  disabled,
  children,
  className,
  ...rest
}: ButtonProps) {
  const classes = [styles.button, VARIANT_CLASS[variant], large ? styles.large : null, className]
    .filter(Boolean)
    .join(' ');

  return (
    <button {...rest} className={classes} disabled={disabled || isLoading}>
      {isLoading && <span className={styles.spinner} aria-hidden="true" />}
      {icon && !isLoading && (
        <span className={styles.buttonIcon} aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}
